// AGC: CreateContainerModal
// AGC: Create a new Silo or Tank inside a warehouse.
// AGC: Capacity entered in user-chosen display unit; backend stores canonical.
// AGC: Optional product assignment from existing inventory items.

import React, { useState, useCallback } from 'react'
import { X, Database, Droplets, Check, Loader2, ChevronDown, Search } from 'lucide-react'
import type { ContainerType, Warehouse, InventoryItem } from '../../types/inventory'
import type { UseWarehouseReturn } from '../../hooks/useWarehouse'
import { clampDecimalStr } from '../../utils'
import toast from 'react-hot-toast'

const SILO_UNITS = ['ton', 'kg', 'sacas']
const TANK_UNITS = ['L', 'gal', 'mL']

interface CreateContainerModalProps {
  isOpen: boolean
  onClose: () => void
  containerType: ContainerType
  warehouses: Warehouse[]
  defaultWarehouseId?: string
  inventoryItems: InventoryItem[]
  warehouseHook: UseWarehouseReturn
}

export const CreateContainerModal: React.FC<CreateContainerModalProps> = ({
  isOpen,
  onClose,
  containerType,
  warehouses,
  defaultWarehouseId,
  inventoryItems,
  warehouseHook,
}) => {
  const isSilo = containerType === 'silo'
  const units = isSilo ? SILO_UNITS : TANK_UNITS

  const [warehouseId, setWarehouseId] = useState(defaultWarehouseId || warehouses[0]?.id || '')
  const [capacityValue, setCapacityValue] = useState('')
  const [capacityUnit, setCapacityUnit] = useState(units[0])
  const [productSearch, setProductSearch] = useState('')
  const [selectedProduct, setSelectedProduct] = useState<InventoryItem | null>(null)
  const [showProducts, setShowProducts] = useState(false)
  const [saving, setSaving] = useState(false)

  const { createContainer } = warehouseHook

  const filteredItems = inventoryItems.filter(item =>
    item.name.toLowerCase().includes(productSearch.toLowerCase())
  )

  const handleCreate = useCallback(async () => {
    if (!warehouseId) { toast.error('Select a warehouse'); return }
    const cap = parseFloat(capacityValue)
    if (!cap || cap <= 0) { toast.error('Enter a valid capacity'); return }

    setSaving(true)
    try {
      await createContainer({
        type: containerType,
        warehouseId,
        capacityValue: cap,
        capacityUnit,
        assignedProductId: selectedProduct?.id,
        assignedProductName: selectedProduct?.name,
      })
      toast.success(`${isSilo ? 'Silo' : 'Tank'} created!`)
      onClose()
    } catch (err: any) {
      toast.error(err.message || 'Failed to create')
    } finally {
      setSaving(false)
    }
  }, [warehouseId, capacityValue, capacityUnit, selectedProduct, containerType, createContainer, isSilo, onClose])

  if (!isOpen) return null

  const Icon = isSilo ? Database : Droplets
  const accentColor = isSilo ? 'amber' : 'blue'

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} role="button" aria-label="Close" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClose()} />
      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl">
        {/* AGC: Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg bg-${accentColor}-100`}>
              <Icon className={`w-5 h-5 text-${accentColor}-600`} />
            </div>
            <h2 className="font-semibold text-gray-900">New {isSilo ? 'Silo' : 'Tank'}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* AGC: Warehouse selector */}
          {warehouses.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Warehouse</label>
              <select
                value={warehouseId}
                onChange={e => setWarehouseId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {warehouses.map(wh => (
                  <option key={wh.id} value={wh.id}>{wh.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* AGC: Capacity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Capacity *</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={capacityValue}
                onChange={e => setCapacityValue(clampDecimalStr(e.target.value))}
                placeholder="e.g. 500"
                min={0.1}
                step="0.01"
                className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                autoFocus
              />
              <select
                value={capacityUnit}
                onChange={e => setCapacityUnit(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {units.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {isSilo ? 'For grain/solid storage (kg, ton, sacas)' : 'For liquid storage (L, gal, mL)'}
            </p>
          </div>

          {/* AGC: Product assignment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assigned Product <span className="font-normal text-gray-400">(optional)</span>
            </label>
            {selectedProduct ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-green-300 bg-green-50">
                <span className="flex-1 text-sm font-medium text-green-800">{selectedProduct.name}</span>
                <button onClick={() => { setSelectedProduct(null); setProductSearch('') }} className="text-green-500 hover:text-green-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={e => { setProductSearch(e.target.value); setShowProducts(true) }}
                    onFocus={() => setShowProducts(true)}
                    placeholder="Search inventory items..."
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                {showProducts && productSearch.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 z-10 bg-white rounded-lg border border-gray-200 shadow-lg max-h-40 overflow-y-auto">
                    {filteredItems.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-400">No items found</div>
                    ) : filteredItems.slice(0, 8).map(item => (
                      <button
                        key={item.id}
                        onClick={() => { setSelectedProduct(item); setProductSearch(''); setShowProducts(false) }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        <span className="font-medium">{item.name}</span>
                        <span className="text-gray-400 text-xs">{item.unit}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-1">One product per container. Can be changed when empty.</p>
          </div>

          {/* AGC: Create button */}
          <button
            onClick={handleCreate}
            disabled={saving || !capacityValue || parseFloat(capacityValue) <= 0}
            className="w-full py-2.5 rounded-xl bg-green-600 text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Create {isSilo ? 'Silo' : 'Tank'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CreateContainerModal
